import { Button, CircularProgress, Skeleton, Typography } from '@mui/material';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import type { FC } from 'react';
import { memo, Suspense, useCallback } from 'react';
import { ConfirmationDialog, SelectApplications } from 'components/common';
import type { GoToTest } from '../types';
import { isEmpty } from 'lodash-es';
import { useEnqueueSnackbar, useModal } from 'hooks';
import { useAddGoToTest, useUpdateGoToTest } from '../api';
import { goToTestEntryValidator } from '../validation';
import type { z } from 'zod';
import { useNavigate } from 'react-router';
import { usePermissionGuard } from 'features/login';
import DeleteGoToTest from './DeleteGoToTest';
import SelectAccounts from './SelectAccounts';
import SelectFlows from './SelectFlows';
import { NoAccessField } from 'components/layout';
import {
  createCabinetMutationTracking,
  trackCabinetSubmitAttempt,
} from 'lib/datadog';

type FormData = z.infer<typeof goToTestEntryValidator.schema>;

interface FormProps {
  data?: GoToTest;
}

const SelectFlowsField = memo(() => {
  const { accessToViewFlow } = usePermissionGuard();
  const { watch } = useFormContext();

  if (!accessToViewFlow)
    return <NoAccessField text={'No access to view flow'} />;
  const app = watch('app') as string;

  return (
    <Suspense
      fallback={
        <Skeleton
          width={'100%'}
          height={'40px'}
          sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
        />
      }
    >
      <SelectFlows
        asAutocomplete
        disabled={!app}
        name={'flow_id'}
        inputLabel={'Flow'}
        placeholder={
          isEmpty(app) ? 'Please select an app first' : 'Select a flow'
        }
        params={{ apps: app ? [app] : undefined }}
      />
    </Suspense>
  );
});
SelectFlowsField.displayName = 'SelectFlowsField';

const SelectAccountsField = memo(() => {
  const { accessToViewFacebookadaccount } = usePermissionGuard();
  const { watch } = useFormContext();
  const app = watch('app') as string;

  if (!accessToViewFacebookadaccount)
    return <NoAccessField text={'No access to view account'} />;

  return (
    <Suspense
      fallback={
        <Skeleton
          width={'100%'}
          height={'40px'}
          sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
        />
      }
    >
      <SelectAccounts
        disabled={!app}
        name={'ad_account_id'}
        asAutocomplete
        inputLabel={'Accounts ID for testing'}
        placeholder={
          isEmpty(app) ? 'Please select an app first' : 'Select an account'
        }
        params={{ apps: app }}
      />
    </Suspense>
  );
});
SelectAccountsField.displayName = 'SelectAccountsField';

const GoToTestForm: FC<FormProps> = ({ data }) => {
  const { accessToDeleteGototest } = usePermissionGuard();
  const { onShowErrorAlert, onShowAlert } = useEnqueueSnackbar();
  const navigate = useNavigate();
  const { open, close, isOpen } = useModal();
  const isEditMode = !isEmpty(data);

  const addGoToTest = useAddGoToTest({
    config: createCabinetMutationTracking({
      platform: 'facebook',
      entity: 'go_to_test',
      mode: 'create',
      onSuccess: () => {
        onShowAlert('Go To Test added successfully');
        navigate('/cabinet/facebook/go-to-test');
      },
      onError: (error) => {
        onShowErrorAlert(error.message);
      },
    }),
  });

  const updateGoToTest = useUpdateGoToTest({
    config: createCabinetMutationTracking({
      platform: 'facebook',
      entity: 'go_to_test',
      mode: 'edit',
      onSuccess: () => {
        onShowAlert('Go To Test updated successfully');
        navigate('/cabinet/facebook/go-to-test');
      },
      onError: (error) => {
        onShowErrorAlert(error.message);
      },
    }),
  });

  const formMethods = useForm<FormData>({
    resolver: goToTestEntryValidator.resolver,
    defaultValues: {
      app: data?.app ?? '',
      flow_id: data?.flow.id ?? '',
      ad_account_id: data?.ad_account.ad_account_id ?? '',
    },
  });

  const {
    handleSubmit,
    trigger,
    resetField,
    formState: { isValid, isDirty },
  } = formMethods;

  const onSubmit = useCallback(
    async (formData: FormData) => {
      const isValid = await trigger();
      if (!isValid) return;

      trackCabinetSubmitAttempt(
        'facebook',
        'go_to_test',
        isEditMode ? 'edit' : 'create',
      );

      const payload = {
        app: formData.app,
        flow_id: formData.flow_id,
        ad_account_id: formData.ad_account_id,
      };

      if (isEditMode) {
        updateGoToTest.mutate({ ...payload, go_to_test_id: Number(data.id) });
      } else {
        addGoToTest.mutate(payload);
      }
    },
    [addGoToTest, updateGoToTest, isEditMode, trigger, data?.id],
  );

  const handleChangeApplications = useCallback(() => {
    resetField('flow_id', { defaultValue: '', keepDirty: false });
    resetField('ad_account_id', { defaultValue: '', keepDirty: false });
  }, [resetField]);

  const isPending = isEditMode
    ? updateGoToTest.isPending
    : addGoToTest.isPending;

  return (
    <>
      <FormProvider {...formMethods}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            maxWidth: '40%',
            alignItems: 'start',
            justifyContent: 'start',
          }}
        >
          <Typography variant={'subtitle1'}>
            {isEditMode ? 'Edit Go To Test' : 'Add Go To Test'}
          </Typography>
          <SelectApplications
            handleChange={handleChangeApplications}
            name={'app'}
            inputLabel={'App'}
          />
          <SelectFlowsField />
          <SelectAccountsField />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              startIcon={
                isPending && <CircularProgress color={'inherit'} size={18} />
              }
              onClick={handleSubmit(onSubmit)}
              variant={'contained'}
              disabled={isPending || !isValid || !isDirty}
            >
              {isEditMode ? 'Update Go to test' : 'Save  Go to test'}
            </Button>
            {isEditMode && data && (
              <>
                <Button
                  variant={'outlined'}
                  color={'error'}
                  onClick={open}
                  disabled={isPending || !accessToDeleteGototest}
                >
                  Delete
                </Button>
                {accessToDeleteGototest && (
                  <DeleteGoToTest
                    isOpen={isOpen}
                    close={close}
                    id={data.id}
                    name={`"${data.app} / ${data.flow.name} / ${data.ad_account.name}"`}
                    open={open}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
        <ConfirmationDialog
          isUnblock={addGoToTest.isSuccess || updateGoToTest.isSuccess}
        />
      </FormProvider>
    </>
  );
};

export default GoToTestForm;
